// frontend/src/components/jobPlacement/PlacementAssessmentForm.jsx
import React, { useEffect, useState, useContext, useRef } from "react";
import { AuthContext } from "../../contexts/AuthContext";
import { getApiBaseURL } from "../../utils/api";
import SignatureField from "../SignatureField";
import secureAxios from "../../utils/secureAxios";
import { toast } from "react-toastify";
import "../../assets/css/signature.css";

const today = new Date().toISOString().split("T")[0];

const initialFormState = {
  assessment_date: today,
  referred_by: "",
  placement_agency_counselor: "",
  family_situation: "",
  education: "",
  transportation: "",
  restrictions: "",
  work_history: "",
  positive_work_traits: "",
  negative_work_traits: "",
  job_preference: "",
  salary_needs: "",
  benefits_needed: "",
  current_benefits_impact: "",
  staff_recommendation: "",
  client_signature_date: today,
  specialist_signature_date: today,
};

const initialConsumerState = {
  date_of_birth: "",
  age: "",
  sex: "",
  marital_status: "",
  phone: "",
};

const PlacementAssessmentForm = ({
  assessment,
  jobPlacement,
  consumer,
  onCancel,
  onSave,
}) => {
  const { user, tenant_id: tenantId } = useContext(AuthContext);
  const apiBase = getApiBaseURL();
  const userId = user?.user_id;

  const [formData, setFormData] = useState(initialFormState);
  const [consumerFields, setConsumerFields] = useState(initialConsumerState);
  const [loading, setLoading] = useState(false);
  const [showSignatures, setShowSignatures] = useState(false);
  const [updateConsumer, setUpdateConsumer] = useState(true);

  // Consumer display data (for name display)
  const [consumerData, setConsumerData] = useState(null);

  // Signature states
  const [clientSignature, setClientSignature] = useState("");
  const [specialistSignature, setSpecialistSignature] = useState("");

  // Signature refs
  const clientSigRef = useRef(null);
  const specialistSigRef = useRef(null);

  // Calculate age from date of birth
  const calculateAge = (dateOfBirth) => {
    if (!dateOfBirth) return "";
    const today = new Date();
    const birthDate = new Date(dateOfBirth);
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (
      monthDiff < 0 ||
      (monthDiff === 0 && today.getDate() < birthDate.getDate())
    ) {
      age--;
    }
    return age >= 0 ? age.toString() : "";
  };

  // Load consumer data for pre-population
  useEffect(() => {
    if (consumer?.consumer_id && tenantId) {
      secureAxios
        .get(
          `${apiBase}/api/placement-assessments/consumer-data/${consumer.consumer_id}`,
          {
            params: { tenant_id: tenantId },
          }
        )
        .then((res) => {
          setConsumerData(res.data);

          // Calculate age from DOB if available, otherwise use stored age
          const dobValue = res.data.date_of_birth || "";
          const ageValue = dobValue
            ? calculateAge(dobValue)
            : res.data.age || "";

          // Set editable consumer fields (address is read-only from consumer)
          setConsumerFields({
            date_of_birth: dobValue,
            age: ageValue,
            sex: res.data.sex || "",
            marital_status: res.data.marital_status || "",
            phone: res.data.phone || "",
          });

          // Pre-populate form with consumer data if creating new assessment
          if (!assessment) {
            setFormData((prev) => ({
              ...prev,
              family_situation: res.data.family_situation || "",
              education: res.data.education || "",
              transportation: res.data.transportation || "",
              restrictions: res.data.restrictions || "",
              work_history: res.data.work_history || "",
              positive_work_traits: res.data.positive_work_traits || "",
              negative_work_traits: res.data.negative_work_traits || "",
              job_preference: res.data.job_preference || "",
              salary_needs: res.data.salary_needs || "",
              benefits_needed: res.data.benefits_needed || "",
            }));
          }
        })
        .catch((err) => {
          console.error("âŒ Error fetching consumer data:", err);
        });
    }
  }, [consumer, tenantId, apiBase, assessment]);

  // Populate form when editing
  useEffect(() => {
    if (assessment) {
      setFormData({
        assessment_date: assessment.assessment_date?.split("T")[0] || today,
        referred_by: assessment.referred_by || "",
        placement_agency_counselor: assessment.placement_agency_counselor || "",
        family_situation: assessment.family_situation || "",
        education: assessment.education || "",
        transportation: assessment.transportation || "",
        restrictions: assessment.restrictions || "",
        work_history: assessment.work_history || "",
        positive_work_traits: assessment.positive_work_traits || "",
        negative_work_traits: assessment.negative_work_traits || "",
        job_preference: assessment.job_preference || "",
        salary_needs: assessment.salary_needs || "",
        benefits_needed: assessment.benefits_needed || "",
        current_benefits_impact: assessment.current_benefits_impact || "",
        staff_recommendation: assessment.staff_recommendation || "",
        client_signature_date:
          assessment.client_signature_date?.split("T")[0] || today,
        specialist_signature_date:
          assessment.specialist_signature_date?.split("T")[0] || today,
      });

      // Reset signatures for delayed loading
      setShowSignatures(false);
      setClientSignature("");
      setSpecialistSignature("");
    } else {
      setFormData(initialFormState);
      setShowSignatures(true);
      setClientSignature("");
      setSpecialistSignature("");
    }
  }, [assessment]);

  // Delayed signature loading for edit mode
  useEffect(() => {
    if (assessment) {
      const timer = setTimeout(() => {
        setShowSignatures(true);
        setTimeout(() => {
          setClientSignature(assessment.client_signature || "");
          setSpecialistSignature(assessment.specialist_signature || "");
        }, 100);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [
    assessment?.assessment_id,
    assessment?.client_signature,
    assessment?.specialist_signature,
  ]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleConsumerFieldChange = (e) => {
    const { name, value } = e.target;

    // Auto-calculate age when date of birth changes
    if (name === "date_of_birth") {
      const calculatedAge = calculateAge(value);
      setConsumerFields((prev) => ({
        ...prev,
        date_of_birth: value,
        age: calculatedAge,
      }));
    } else {
      setConsumerFields((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    // Get signatures
    let finalClientSig = clientSignature;
    let finalSpecialistSig = specialistSignature;

    if (clientSigRef.current) {
      const currentSig = clientSigRef.current.getCurrentSignature?.();
      if (currentSig) finalClientSig = currentSig;
    }
    if (specialistSigRef.current) {
      const currentSig = specialistSigRef.current.getCurrentSignature?.();
      if (currentSig) finalSpecialistSig = currentSig;
    }

    // Fallback for edit mode
    if (!finalClientSig && assessment)
      finalClientSig = assessment.client_signature || "";
    if (!finalSpecialistSig && assessment)
      finalSpecialistSig = assessment.specialist_signature || "";

    const payload = {
      ...formData,
      job_placement_id: jobPlacement.job_placement_id,
      consumer_id: consumer.consumer_id,
      tenant_id: tenantId,
      created_by: userId,
      client_signature: finalClientSig,
      specialist_signature: finalSpecialistSig,
      update_consumer: updateConsumer,
      // Include consumer demographic fields (address is managed in Consumer record)
      consumer_date_of_birth: consumerFields.date_of_birth,
      consumer_age: consumerFields.age,
      consumer_sex: consumerFields.sex,
      consumer_marital_status: consumerFields.marital_status,
      consumer_phone: consumerFields.phone,
    };

    try {
      let response;
      let assessmentId;

      if (assessment) {
        response = await secureAxios.put(
          `${apiBase}/api/placement-assessments/${assessment.assessment_id}`,
          payload
        );
        assessmentId = assessment.assessment_id;
        toast.success("Placement Assessment updated successfully");
      } else {
        response = await secureAxios.post(
          `${apiBase}/api/placement-assessments`,
          payload
        );
        assessmentId = response.data.assessment_id;
        toast.success("Placement Assessment created successfully");
      }

      // Generate PDF
      try {
        const pdfRes = await secureAxios.get(
          `${apiBase}/api/placement-assessments/pdf/${assessmentId}`,
          { params: { tenant_id: tenantId } }
        );
        if (pdfRes.data?.pdf_url) {
          toast.info("PDF generated and saved to Document Library");
        }
      } catch (pdfErr) {
        console.error("PDF generation failed:", pdfErr);
      }

      if (onSave) onSave();
    } catch (err) {
      console.error("âŒ Error saving assessment:", err);
      toast.error(err.response?.data?.error || "Failed to save assessment");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container my-4">
    <div className="placement-assessment-form">
      <div className="d-flex flex-column flex-sm-row justify-content-between align-items-start align-items-sm-center mb-2 mb-md-3 gap-2">
        <h5>
          <i className="bi bi-clipboard-check me-2"></i>
          {assessment ? "Edit" : "Create"} CRP-31: Placement Services Assessment
        </h5>
        <button className="btn btn-outline-secondary btn-sm" onClick={onCancel}>
          <i className="bi bi-arrow-left me-1"></i> Back
        </button>
      </div>

      {/* Consumer Info Card - Demographics and Phone are editable, Address comes from Consumer record */}
      <div className="card mb-2 mb-md-3">
        <div className="card-header bg-secondary text-white py-2">
          <i className="bi bi-person me-2"></i>
          Consumer Information
          <small className="ms-2 opacity-75">
            (Edit demographics/phone - Address from Consumer record)
          </small>
        </div>
        <div className="card-body p-2">
          <div className="row g-2">
            <div className="col-md-3">
              <label className="form-label small mb-1">Consumer Name</label>
              <input
                type="text"
                className="form-control form-control-sm"
                value={consumer?.name || consumerData?.name || ""}
                disabled
                style={{ backgroundColor: "#e9ecef" }}
              />
            </div>
            <div className="col-md-2">
              <label className="form-label small mb-1">Sex</label>
              <select
                name="sex"
                className="form-select form-select-sm"
                value={consumerFields.sex}
                onChange={handleConsumerFieldChange}
              >
                <option value="">-</option>
                <option value="Male">Male</option>
                <option value="Female">Female</option>
                <option value="Other">Other</option>
              </select>
            </div>
            <div className="col-md-2">
              <label className="form-label small mb-1">Marital Status</label>
              <select
                name="marital_status"
                className="form-select form-select-sm"
                value={consumerFields.marital_status}
                onChange={handleConsumerFieldChange}
              >
                <option value="">-</option>
                <option value="Single">Single</option>
                <option value="Married">Married</option>
                <option value="Divorced">Divorced</option>
                <option value="Widowed">Widowed</option>
                <option value="Separated">Separated</option>
              </select>
            </div>
            <div className="col-md-2">
              <label className="form-label small mb-1">
                Date of Birth
                <i
                  className="bi bi-shield-lock text-warning ms-1"
                  title="PHI"
                ></i>
              </label>
              <input
                type="date"
                name="date_of_birth"
                className="form-control form-control-sm"
                value={consumerFields.date_of_birth}
                onChange={handleConsumerFieldChange}
              />
            </div>
            <div className="col-4 col-md-1">
              <label className="form-label small mb-1">Age</label>
              <input
                type="text"
                name="age"
                className="form-control form-control-sm"
                value={consumerFields.age}
                onChange={handleConsumerFieldChange}
                placeholder="-"
                disabled={!!consumerFields.date_of_birth}
                readOnly={!!consumerFields.date_of_birth}
                title={
                  consumerFields.date_of_birth
                    ? "Auto-calculated from DOB"
                    : "Enter age or provide DOB"
                }
              />
            </div>
            <div className="col-md-2">
              <label className="form-label small mb-1">Phone</label>
              <input
                type="text"
                name="phone"
                className="form-control form-control-sm"
                value={consumerFields.phone}
                onChange={handleConsumerFieldChange}
                placeholder="(xxx) xxx-xxxx"
              />
            </div>
            <div className="col-12">
              <label className="form-label small mb-1">
                Present Address{" "}
                <small className="text-muted">(from Consumer record)</small>
              </label>
              <input
                type="text"
                className="form-control form-control-sm"
                value={consumerData?.address || ""}
                disabled
                style={{ backgroundColor: "#e9ecef" }}
              />
            </div>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="row g-2 g-md-3">
          {/* Assessment Date */}
          <div className="col-md-4">
            <label className="form-label">Assessment Date</label>
            <input
              type="date"
              name="assessment_date"
              className="form-control form-control-sm"
              value={formData.assessment_date}
              onChange={handleChange}
            />
          </div>

          {/* Referral Info */}
          <div className="col-md-4">
            <label className="form-label">
              Referred to Placement Agency by
            </label>
            <input
              type="text"
              name="referred_by"
              className="form-control form-control-sm"
              value={formData.referred_by}
              onChange={handleChange}
            />
          </div>

          <div className="col-md-4">
            <label className="form-label">Placement Agency Counselor</label>
            <input
              type="text"
              name="placement_agency_counselor"
              className="form-control form-control-sm"
              value={formData.placement_agency_counselor}
              onChange={handleChange}
            />
          </div>

          {/* Consumer Assessment Fields */}
          <div className="col-12">
            <label className="form-label">Family Situation</label>
            <textarea
              name="family_situation"
              className="form-control form-control-sm"
              rows="2"
              value={formData.family_situation}
              onChange={handleChange}
            />
          </div>

          <div className="col-md-6">
            <label className="form-label">Education</label>
            <textarea
              name="education"
              className="form-control form-control-sm"
              rows="2"
              value={formData.education}
              onChange={handleChange}
            />
          </div>

          <div className="col-md-6">
            <label className="form-label">Transportation</label>
            <textarea
              name="transportation"
              className="form-control form-control-sm"
              rows="2"
              value={formData.transportation}
              onChange={handleChange}
            />
          </div>

          <div className="col-12">
            <label className="form-label">Restrictions (if any)</label>
            <textarea
              name="restrictions"
              className="form-control form-control-sm"
              rows="2"
              value={formData.restrictions}
              onChange={handleChange}
            />
          </div>

          <div className="col-12">
            <label className="form-label">Work History</label>
            <textarea
              name="work_history"
              className="form-control form-control-sm"
              rows="3"
              value={formData.work_history}
              onChange={handleChange}
            />
          </div>

          <div className="col-md-6">
            <label className="form-label">Positive Work Traits</label>
            <textarea
              name="positive_work_traits"
              className="form-control form-control-sm"
              rows="3"
              value={formData.positive_work_traits}
              onChange={handleChange}
            />
          </div>

          <div className="col-md-6">
            <label className="form-label">Negative Work Traits</label>
            <textarea
              name="negative_work_traits"
              className="form-control form-control-sm"
              rows="3"
              value={formData.negative_work_traits}
              onChange={handleChange}
            />
          </div>

          <div className="col-md-4">
            <label className="form-label">Client's Job Preference</label>
            <input
              type="text"
              name="job_preference"
              className="form-control form-control-sm"
              value={formData.job_preference}
              onChange={handleChange}
            />
          </div>

          <div className="col-md-4">
            <label className="form-label">Client's Salary Needs</label>
            <input
              type="text"
              name="salary_needs"
              className="form-control form-control-sm"
              value={formData.salary_needs}
              onChange={handleChange}
            />
          </div>

          <div className="col-md-4">
            <label className="form-label">Benefits Needed</label>
            <input
              type="text"
              name="benefits_needed"
              className="form-control form-control-sm"
              value={formData.benefits_needed}
              onChange={handleChange}
            />
          </div>

          <div className="col-12">
            <label className="form-label">
              Current Benefits/Impact of Placement on Benefits
            </label>
            <textarea
              name="current_benefits_impact"
              className="form-control form-control-sm"
              rows="2"
              value={formData.current_benefits_impact}
              onChange={handleChange}
            />
          </div>

          <div className="col-12">
            <label className="form-label">
              Staff Recommendation Regarding Competitive Employment Potential
            </label>
            <textarea
              name="staff_recommendation"
              className="form-control form-control-sm"
              rows="3"
              value={formData.staff_recommendation}
              onChange={handleChange}
            />
          </div>

          {/* Update Consumer Checkbox */}
          <div className="col-12">
            <div className="form-check">
              <input
                type="checkbox"
                className="form-check-input"
                id="updateConsumer"
                checked={updateConsumer}
                onChange={(e) => setUpdateConsumer(e.target.checked)}
              />
              <label className="form-check-label" htmlFor="updateConsumer">
                Save all data to consumer's profile (Demographics, Education,
                Work History, etc.)
              </label>
            </div>
          </div>

          {/* Signatures */}
          <div className="col-md-6">
            <label className="form-label">Client's Signature</label>
            {showSignatures ? (
              <SignatureField
                ref={clientSigRef}
                value={clientSignature}
                onChange={setClientSignature}
                penColor="blue"
                label=""
              />
            ) : (
              <div
                style={{
                  height: "150px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <span className="text-muted">Loading signature...</span>
              </div>
            )}
            <input
              type="date"
              name="client_signature_date"
              className="form-control mt-2"
              value={formData.client_signature_date}
              onChange={handleChange}
            />
          </div>

          <div className="col-md-6">
            <label className="form-label">
              Placement Specialist's Signature
            </label>
            {showSignatures ? (
              <SignatureField
                ref={specialistSigRef}
                value={specialistSignature}
                onChange={setSpecialistSignature}
                penColor="blue"
                label=""
              />
            ) : (
              <div
                style={{
                  height: "150px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <span className="text-muted">Loading signature...</span>
              </div>
            )}
            <input
              type="date"
              name="specialist_signature_date"
              className="form-control mt-2"
              value={formData.specialist_signature_date}
              onChange={handleChange}
            />
          </div>

          {/* Submit Buttons */}
          <div className="col-12 mt-4">
            <button
              type="submit"
              className="btn btn-primary me-2"
              disabled={loading}
            >
              {loading ? (
                <>
                  <span className="spinner-border spinner-border-sm me-1"></span>
                  Saving...
                </>
              ) : (
                <>
                  <i className="bi bi-check-circle me-1"></i>
                  {assessment ? "Update Assessment" : "Create Assessment"}
                </>
              )}
            </button>
            <button
              type="button"
              className="btn btn-outline-secondary"
              onClick={onCancel}
            >
              Cancel
            </button>
          </div>
        </div>
      </form>
    </div>
    </div>
  );
};

export default PlacementAssessmentForm;